import React from 'react'
import { Sidebar } from './sidebar'
import { Topbar } from './topbar'
import { BottomNav } from './bottom-nav'
import { DashboardPage } from './pages_dashboard'
import { ConsoleTab, OverviewTab, ServerHeader } from './pages_server'
import { QuickActionsTab } from './pages_quick'
import { AuditPage, LifecycleModal, LoginPage, NotAuthorizedPage, PlayersPage, SettingsPage } from './pages_misc'
import { InstalledRow, PluginsPage } from './pages_plugins'
import { PlayersTab } from './pages_players'
import { PropertiesPage } from './pages_properties'
import { BackupsPage } from './pages_backups'
import { Icon } from './icons'
import { ToastHost, useToasts } from './shared'
import { usePanelLogic } from './panel_logic'
import type { AppState, InstalledPlugin, LifecycleAction, PendingAction, Role, Server, ServerId } from './types'

interface ServerPluginsTabProps {
  server: Server
  role: Role
  installed: InstalledPlugin[]
  onToggle: (plugin: InstalledPlugin) => void
  onDelete: (plugin: InstalledPlugin) => void
}

function ServerPluginsTab({ server, role, installed, onToggle, onDelete }: ServerPluginsTabProps) {
  const canAct = role === 'owner' || role === 'admin'
  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
        <Icon name="server" size={14} />
        <span className="mono" style={{ fontWeight: 600 }}>{server.name}</span>
        <span className="ml-auto dim text-xs">{installed.length} plugin{installed.length !== 1 ? 's' : ''}</span>
      </div>
      {installed.length === 0 ? (
        <div className="dim text-sm" style={{ padding: 24, textAlign: 'center' }}>No plugins installed.</div>
      ) : (
        installed.map(plugin => (
          <InstalledRow
            key={`${server.id}-${plugin.filename ?? plugin.name}`}
            plugin={plugin}
            canAct={canAct}
            onToggle={onToggle}
            onDelete={onDelete}
          />
        ))
      )}
    </div>
  )
}

interface TweaksPanelProps {
  state: AppState
  setState: React.Dispatch<React.SetStateAction<AppState>>
  onClose: () => void
}

function TweaksPanel({ state, setState, onClose }: TweaksPanelProps) {
  return (
    <div className="tweaks-panel">
      <div className="tweaks-header">
        <span>Tweaks</span>
        <button className="btn sm ghost" onClick={onClose}><Icon name="x" size={11} /></button>
      </div>
      <div className="tweaks-body">
        <div className="tweak-row">
          <label>Dashboard layout</label>
          <div className="seg">
            {([['cards', 'Cards'], ['dense', 'Dense'], ['hero', 'Hero']] as const).map(([value, label]) => (
              <button key={value} className={state.dashLayout === value ? 'active' : ''} onClick={() => setState(current => ({ ...current, dashLayout: value }))}>{label}</button>
            ))}
          </div>
        </div>
        <div className="tweak-row">
          <label>Accent color</label>
          <div className="swatches">
            {[
              ['#6366f1', '#4f46e5', '#818cf8'],
              ['#8b5cf6', '#7c3aed', '#a78bfa'],
              ['#10b981', '#059669', '#34d399'],
              ['#f59e0b', '#d97706', '#fbbf24'],
              ['#ec4899', '#db2777', '#f472b6'],
            ].map(([accent, accent600, accent400], index) => (
              <div
                key={index}
                className={`swatch ${state.accent === accent ? 'active' : ''}`}
                style={{ background: `linear-gradient(135deg, ${accent}, ${accent600})` }}
                onClick={() => {
                  document.documentElement.style.setProperty('--accent', accent)
                  document.documentElement.style.setProperty('--accent-600', accent600)
                  document.documentElement.style.setProperty('--accent-400', accent400)
                  setState(current => ({ ...current, accent }))
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

function normalizeStoredRoute(route: string | null): string {
  if (!route || route === 'login' || route === 'not-authorized') return 'dashboard'
  return route
}

function pickServerForPlayer(playerName: string, servers: Server[]): Server | null {
  return servers.find(server => server.players.includes(playerName)) ?? servers[0] ?? null
}

export function App() {
  const defaults: AppState = {
    dashLayout: 'cards',
    accent: '#6366f1',
  }

  const [state, setState] = React.useState<AppState>(() => {
    try {
      return { ...defaults, ...JSON.parse(localStorage.getItem('mcpanel_state') || '{}') }
    } catch {
      return defaults
    }
  })
  const [route, setRoute] = React.useState<string>(() => normalizeStoredRoute(localStorage.getItem('mcpanel_route')))
  const [pendingAction, setPendingAction] = React.useState<PendingAction | null>(null)
  const [tweaksOpen, setTweaksOpen] = React.useState(false)
  const [navOpen, setNavOpen] = React.useState(false)
  const { toasts, push } = useToasts()
  const panel = usePanelLogic(push)

  const role = (panel.viewer?.role ?? 'player') as Role

  React.useEffect(() => {
    localStorage.setItem('mcpanel_state', JSON.stringify(state))
  }, [state])

  React.useEffect(() => {
    localStorage.setItem('mcpanel_route', route)
  }, [route])

  React.useEffect(() => {
    const swatches: Record<string, [string, string]> = {
      '#6366f1': ['#4f46e5', '#818cf8'],
      '#8b5cf6': ['#7c3aed', '#a78bfa'],
      '#10b981': ['#059669', '#34d399'],
      '#f59e0b': ['#d97706', '#fbbf24'],
      '#ec4899': ['#db2777', '#f472b6'],
    }
    const colors = swatches[state.accent] || swatches['#6366f1']
    document.documentElement.style.setProperty('--accent', state.accent)
    document.documentElement.style.setProperty('--accent-600', colors[0])
    document.documentElement.style.setProperty('--accent-400', colors[1])
  }, [state.accent])

  React.useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (!event.data || typeof event.data !== 'object') return
      if (event.data.type === '__activate_edit_mode') setTweaksOpen(true)
      if (event.data.type === '__deactivate_edit_mode') setTweaksOpen(false)
    }
    window.addEventListener('message', handler)
    window.parent.postMessage({ type: '__edit_mode_available' }, '*')
    return () => window.removeEventListener('message', handler)
  }, [])

  const setTweaksState: React.Dispatch<React.SetStateAction<AppState>> = updater => {
    setState(prev => {
      const next = typeof updater === 'function' ? (updater as (value: AppState) => AppState)(prev) : updater
      window.parent.postMessage({ type: '__edit_mode_set_keys', edits: next }, '*')
      return next
    })
  }

  const confirmAction = ({ announce }: { announce: boolean }) => {
    if (!pendingAction) return
    void panel.runLifecycleAction(pendingAction.server.id, pendingAction.action, announce)
    setPendingAction(null)
  }

  const onServerAction = (action: LifecycleAction, server: Server) => {
    setPendingAction({ action, server })
  }

  if (panel.meLoading) {
    return (
      <>
        <div className="login-wrap">
          <div className="login-card">
            <div className="big-logo">M</div>
            <div className="tag">Loading…</div>
          </div>
        </div>
        <ToastHost toasts={toasts} />
      </>
    )
  }

  if (panel.session.status === 'anonymous') {
    return (
      <>
        <LoginPage onLogin={panel.login} />
        <ToastHost toasts={toasts} />
      </>
    )
  }

  if (panel.session.status === 'unauthorized') {
    return (
      <>
        <NotAuthorizedPage onSignOut={() => void panel.logout()} />
        <ToastHost toasts={toasts} />
      </>
    )
  }

  let serverId: ServerId | null = null
  let serverTab = 'overview'
  if (route.startsWith('srv:')) {
    const parts = route.split(':')
    serverId = parts[1] || null
    serverTab = parts[2] || 'overview'
  }
  const activeServer = panel.servers.find(server => server.id === serverId) ?? null

  const renderMain = () => {
    if (activeServer && panel.viewer) {
      const setTab = (tab: string) => setRoute(`srv:${activeServer.id}:${tab}`)
      return (
        <>
          <ServerHeader server={activeServer} tab={serverTab} setTab={setTab} />
          {serverTab === 'overview' && (
            <OverviewTab
              server={activeServer}
              role={role}
              users={panel.users}
              capabilities={{
                canDownloadWorld: panel.capabilities.canDownloadWorld,
                canDownloadLogs: panel.capabilities.canDownloadLogs,
              }}
              onAction={onServerAction}
            />
          )}
          {serverTab === 'console' && (
            <ConsoleTab
              server={activeServer}
              role={role}
              logs={panel.logs[activeServer.id] || []}
              onSendCommand={input => panel.sendConsoleCommand(activeServer.id, input)}
              onClear={() => panel.clearConsole(activeServer.id)}
              toast={push}
            />
          )}
          {serverTab === 'quick' && (
            <QuickActionsTab
              server={activeServer}
              role={role}
              viewer={panel.viewer}
              onRunAction={request => void panel.runQuickAction(activeServer.id, request)}
            />
          )}
          {serverTab === 'players' && (
            <PlayersTab
              server={activeServer}
              role={role}
              onFetchLive={signal => panel.fetchLivePlayers(activeServer.id, signal)}
              onFetchOffline={signal => panel.fetchOfflinePlayers(activeServer.id, signal)}
              canEditInventory={panel.capabilities.canEditInventory}
              onFetchRegistry={signal => panel.fetchItemRegistry(activeServer.id, signal)}
              onEditInventory={(playerName, body) => panel.editPlayerInventory(activeServer.id, playerName, body)}
              onFetchShulker={(playerName, slot) => panel.fetchShulkerContents(activeServer.id, playerName, slot)}
              onEditShulker={(playerName, slot, body) => panel.editShulkerContents(activeServer.id, playerName, slot, body)}
              onFetchPeople={() => panel.fetchPeople(activeServer.id)}
              onMutatePeople={(kind, action, body, verb) => panel.mutatePeople(activeServer.id, kind, action, body, verb)}
            />
          )}
          {serverTab === 'properties' && (
            <PropertiesPage
              serverId={activeServer.id}
              serverName={activeServer.name}
              role={role}
              capabilities={panel.capabilities}
              onFetch={() => panel.fetchProperties(activeServer.id)}
              onWrite={body => panel.writeProperties(activeServer.id, body)}
            />
          )}
          {serverTab === 'plugins' && (
            <ServerPluginsTab
              server={activeServer}
              role={role}
              installed={panel.installedPlugins[activeServer.id] || []}
              onToggle={plugin => void panel.toggleInstalledPlugin(activeServer.id, plugin)}
              onDelete={plugin => void panel.deleteInstalledPlugin(activeServer.id, plugin)}
            />
          )}
        </>
      )
    }

    switch (route) {
      case 'dashboard':
        return (
          <DashboardPage
            servers={panel.servers}
            vps={panel.vps}
            role={role}
            setRoute={setRoute}
            onAction={onServerAction}
            layout={state.dashLayout}
            serversError={panel.serversError}
            serversLoading={panel.serversLoading}
            onRetry={panel.refetchServers}
          />
        )
      case 'players':
        return (
          <PlayersPage
            role={role}
            players={panel.players}
            loading={panel.playersLoading}
            error={panel.playersError}
            onRetry={panel.refetchPlayers}
            onHeal={playerName => {
              const server = pickServerForPlayer(playerName, panel.servers)
              if (server) {
                void panel.runQuickAction(server.id, { template: 'heal_player', payload: { target: playerName } })
              }
            }}
            toast={push}
          />
        )
      case 'plugins':
        return (
          <PluginsPage
            role={role}
            pending={panel.pendingPlugins}
            installed={panel.installedPlugins}
            servers={panel.servers}
            loading={panel.pluginsLoading}
            error={panel.pluginsError}
            onRetry={panel.refetchPlugins}
            onApprove={panel.approvePlugin}
            onReject={panel.rejectPlugin}
            onToggle={panel.toggleInstalledPlugin}
            onDelete={panel.deleteInstalledPlugin}
            onUpload={panel.uploadPlugin}
          />
        )
      case 'backups':
        return (
          <BackupsPage
            role={role}
            capabilities={panel.capabilities}
            servers={panel.servers}
            data={panel.backups}
            loading={panel.backupsLoading}
            error={panel.backupsError}
            onRetry={panel.refetchBackups}
            onTrigger={panel.triggerBackup}
            onRestore={panel.restoreBackup}
          />
        )
      case 'audit':
        return (
          <AuditPage
            role={role}
            rows={panel.audit}
            users={panel.users}
            loading={panel.auditLoading}
            error={panel.auditError}
            onRetry={panel.refetchAudit}
          />
        )
      case 'settings':
        return role === 'owner'
          ? (
            <SettingsPage
              users={panel.users}
              loading={panel.usersLoading}
              error={panel.usersError}
              onRetry={panel.refetchUsers}
              onSaveMapping={panel.saveMapping}
              onRevokeAllSessions={() => void panel.revokeAllSessions()}
              onCreateUser={panel.createUser}
              onUpdateUser={panel.updateUser}
              onDeleteUser={panel.deleteUser}
              toast={push}
            />
          )
          : <div className="card" style={{ textAlign: 'center', padding: 40 }}><Icon name="shield" size={26} style={{ opacity: 0.5 }} /><h3>403 - Owner only</h3><div className="dim">You do not have permission for that.</div></div>
      default:
        return (
          <DashboardPage
            servers={panel.servers}
            vps={panel.vps}
            role={role}
            setRoute={setRoute}
            onAction={onServerAction}
            layout={state.dashLayout}
            serversError={panel.serversError}
            serversLoading={panel.serversLoading}
            onRetry={panel.refetchServers}
          />
        )
    }
  }

  return (
    <div className="app">
      {panel.devMode && (
        <div className="dev-banner" role="status" aria-label="Development mode">
          <span className="dev-banner-tag">DEV</span>
          <span>Local Paper · lifecycle &amp; backups stubbed · this is NOT the VPS</span>
        </div>
      )}
      <Topbar
        route={route}
        servers={panel.servers}
        role={role}
        viewer={panel.viewer}
        onMenuClick={() => setNavOpen(true)}
      />
      <Sidebar
        route={route}
        setRoute={setRoute}
        role={role}
        viewer={panel.viewer}
        servers={panel.servers}
        onSignOut={() => void panel.logout()}
        open={navOpen}
        onNavigate={() => setNavOpen(false)}
      />
      {navOpen && <div className="sidebar-backdrop" onClick={() => setNavOpen(false)} />}
      <main className="main">
        <div className="main-inner">
          {renderMain()}
        </div>
      </main>
      <BottomNav
        route={route}
        servers={panel.servers}
        setRoute={setRoute}
        onMoreClick={() => setNavOpen(true)}
      />
      <LifecycleModal
        action={pendingAction?.action}
        server={pendingAction?.server}
        onClose={() => setPendingAction(null)}
        onConfirm={confirmAction}
      />
      {tweaksOpen && <TweaksPanel state={state} setState={setTweaksState} onClose={() => setTweaksOpen(false)} />}
      {!tweaksOpen && (
        <button className="tweaks-toggle" onClick={() => setTweaksOpen(true)} title="Open Tweaks panel">
          <Icon name="sliders" size={16} />
        </button>
      )}
      <ToastHost toasts={toasts} />
    </div>
  )
}
